import CloseIcon from "../icons/CloseIcon";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import SuperDropdown from "../dropdown/SuperDropdown";
import DefaultDropdownItem from "../dropdown/DefaultDropdownItem";
import DefaultLoadingError from "../error/DefaultLoadingError";
import Spinner from "../spinner/Spinner";

interface IFormControlProps {
  value: string | undefined;
  className?: string;
  onChange: (id: string) => void;
  queryValues: IFormControlGenericQueryValues;
  queryKeys?: (keyof IFormControlData)[];
  messageNode?: {
    error?: ReactNode;
    loading?: ReactNode;
    noData?: ReactNode;
  };
}

const AutoCompleteFormControl = ({
  className,
  value,
  onChange,
  queryValues: { isPending, error, data },
  messageNode,
}: IFormControlProps): ReactNode => {
  const [shouldFocusInputsOnMount, setShouldFocusInputsOnMount] =
    useState(false);
  if (error) {
    return (
      messageNode?.error ?? (
        <DefaultLoadingError label="Kunne ikke hente data fra service" />
      )
    );
  }
  if (isPending) {
    return (
      messageNode?.loading ?? (
        <Spinner
          inline
          label="Henter data"
          className="form-control justify-content-start"
          size={14}
        />
      )
    );
  }
  if (!data) return messageNode?.noData ?? <div>Noget andet gik galt</div>;

  if (value) {
    const title =
      data.find((u) => u.id.toLowerCase() === value.toLowerCase())?.navn ??
      "undefined";
    return (
      <ValueButton
        title={title}
        onClick={() => {
          setShouldFocusInputsOnMount(true);
          onChange("");
        }}
        shouldFocusOnMount={shouldFocusInputsOnMount}
      />
    );
  }

  data.sort((a, b) => a.navn.localeCompare(b.navn));

  return (
    <SuperDropdown
      classNameContainer={`w-100${className ? ` ${className}` : ""}`}
      filterKeys={["navn"]}
      items={data}
      itemsIdKey="id"
      itemToString={(item) => item?.navn ?? ""}
      itemToElement={(item) => <DefaultDropdownItem label={item.navn} />}
      onSelectedItemEvent={(event) => {
        if (event.selectedItem) {
          setShouldFocusInputsOnMount(true);
          onChange(event.selectedItem.id);
        }
      }}
      focusInputOnMount={shouldFocusInputsOnMount}
    />
  );
};

export default AutoCompleteFormControl;

function ValueButton(props: {
  title: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  shouldFocusOnMount: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const { onClick, title, shouldFocusOnMount } = props;
  useLayoutEffect(() => {
    if (shouldFocusOnMount) ref.current?.focus();
  }, [shouldFocusOnMount]);
  return (
    <button
      ref={ref}
      className="form-control d-flex align-items-center justify-content-between"
      onClick={onClick}
    >
      <span>{title}</span>
      <CloseIcon width={16} />
    </button>
  );
}
